import { GOOGLE_FONTS } from '@/lib/schemas/brandKit';

/**
 * El vocabulario de diseño que una app consumidora puede tocar.
 *
 * Es el espejo de `ox-xurvey/src/contracts/themes.ts` y `SurveyDesign`, y está
 * aquí por lo mismo que el canal: un paquete npm entre las dos apps devolvería
 * el acoplamiento que este ecosistema lleva un refactor entero quitándose.
 *
 * **Lo que NO está en estas listas no se puede tocar, y eso es el contrato**
 * (protocolo §3), no un descuido: ni etiquetas, ni posiciones, ni componentes.
 * Añadir aquí una clave que Xurvey no entienda no rompe nada —la ignora— pero
 * miente a quien la use, así que se amplían los dos ficheros a la vez.
 */

/** Los nueve colores del tema. El orden es el que se pinta en el panel. */
export const TOKENS_COLOR = [
  { key: 'bg', label: 'Background' },
  { key: 'surface', label: 'Surface' },
  { key: 'primary', label: 'Primary' },
  { key: 'onPrimary', label: 'On primary' },
  { key: 'accent', label: 'Accent' },
  { key: 'text', label: 'Text' },
  { key: 'muted', label: 'Muted text' },
  { key: 'border', label: 'Border' },
  { key: 'danger', label: 'Error' },
] as const;

export type TokenColor = (typeof TOKENS_COLOR)[number]['key'];

/**
 * Tipografías disponibles: las mismas que Brand Studio y que el editor de
 * Xurvey (`GOOGLE_FONTS` en los dos). El runtime de Xurvey carga bajo demanda
 * la que pida el diseño, así que cualquiera de éstas se ve también en el móvil
 * del invitado, no sólo en un escritorio que la tenga instalada.
 */
export const FUENTES = GOOGLE_FONTS;

/** Celebración al ganar un premio. `none` la desactiva. */
export const CELEBRACIONES = ['confetti', 'fireworks', 'sparks', 'none'] as const;

/** Cómo se pinta el progreso. Vacío = lo que diga la plantilla. */
export const PAGINADOS = ['numeric', 'dots', 'bar', 'none'] as const;

/** Cómo se colocan «Atrás» y «Continuar». */
export const NAV_LAYOUTS = ['vertical', 'horizontal'] as const;

/** Cómo ocupa el paso la pantalla. */
export const LAYOUT_FIT = ['expand', 'fit'] as const;
export const LAYOUT_ALIGN = ['top', 'center', 'bottom'] as const;

/** Encuadre del fondo. `cover` para degradados e imágenes; una medida repite. */
export const FONDO_TAMANOS = ['cover', 'contain', '22px 22px', '48px 48px'] as const;


/**
 * `url("…")` → `…`.
 *
 * El fondo se guarda como valor CSS —puede ser un degradado o un color— y el
 * uploader trabaja con la ruta pelada. Vive aquí y no en un panel porque lo
 * usan los dos: el del formulario y el del cartel.
 */
export const desnudo = (v: string | null) => {
  if (!v) return null;
  const m = /^url\(["']?(.*?)["']?\)$/.exec(v.trim());
  return m ? (m[1] ?? null) : v;
};
